<template>
  <ViewContainer
    :breadcrumb="breadcrumbs"
    :key="organization._id"
    :title="
      $t('organization_users.title', {
        organizationName: organization.name,
      })
    "
  >
    <template v-slot:header-right>
      <CreateButton
        small
        testId="people-button-addUsers"
        :disabled="!canAddUsers"
        :tooltipText="$t('common.button.add')"
        @click="showModal('userModalShow')"
      />
    </template>

    <UserLicensesCard
      v-if="organization._id"
      :organizationId="organization._id"
    />

    <v-card style="min-height: 600px" class="ma-0 pa-0 px-6">
      <v-card-title class="px-0 mx-0">
        <v-row>
          <v-col cols="4">
            <v-btn
              rounded
              small
              depressed
              class="me-2"
              data-testid="people-button-toggleFilters"
              data-tourid="people-button-toggleFilters"
              :outlined="!showFilters"
              :color="showFilters ? 'primary' : 'blue-grey'"
              @click="showFilters = !showFilters"
            >
              <v-icon left>mdi-tune</v-icon>
              <p class="mr-1 ma-0 pa-0">
                {{
                  showFilters
                    ? $t('explore.hide_filters')
                    : $t('explore.show_filters')
                }}
              </p>
              <p class="ma-0 pa-0" v-if="activeFiltersCount">
                ({{ activeFiltersCount }})
              </p>
            </v-btn>
            <Button
              v-if="activeFiltersCount && showFilters"
              plain
              color="primary"
              size="small"
              data-testid="people-button-clearAllFilters"
              icon="mdi-close"
              :text="$t('common.clear_all')"
              :disabled="!hasFiltersSelected"
              @click="clearAllFilters"
            />
          </v-col>
        </v-row>

        <div class="d-flex justify-space-between align-center">
          <div class="d-flex justify-space-between">
            <SearchInput
              class="me-4"
              :value="searchTerms"
              @change="handleSearch"
            />
            <ColumnsSelector
              persistenceKey="organizationUsers"
              :headers="headers"
              @toggleColumn="onToggleColumn"
              @setInitialColumns="setInitialColumns"
            />
            <div class="ms-4">
              <OrganizationMenu :organizationId="organization._id" />
            </div>
          </div>
        </div>
      </v-card-title>

      <v-card-text class="mx-0 px-0">
        <v-row class="ma-0 pa-0">
          <v-col v-show="showFilters" cols="12" :md="3" class="mx-0 px-0 pe-4">
            <PeopleFilters
              v-if="organizationStructure"
              :filters="selectedFilters"
              :organizationStructure="organizationStructure"
              :organizationId="organizationId"
              @change="onFiltersUpdate"
            />
          </v-col>
          <v-col cols="12" class="px-0" :md="showFilters ? 9 : 12">
            <div
              v-if="searchTerms && !users.length"
              class="d-flex align-center justify-center"
              style="min-height: 600px"
            >
              <EmptyState />
            </div>

            <v-card v-else outlined rounded>
              <v-data-table
                id="data-users-table"
                data-private
                calculate-widths
                :headers="columns"
                :items="users"
                :options.sync="options"
                :server-items-length="totalRecordsInData"
                :loading="isLoading"
                :footer-props="footerOptions"
              >
                <template v-slot:item.displayname="{ item }">
                  <ChipAvatar
                    link
                    outlined
                    small
                    :vuetifyProps="{
                      link: true,
                      to: {
                        name: 'profile',
                        params: {
                          organizationId: item.organization._id,
                          userId: item._id,
                        },
                      },
                      disabled: item.isDeleted,
                    }"
                    :account="item"
                  />
                </template>
                <template v-slot:item.email="{ item }">
                  <p
                    class="ma-0 pa-0 text-body-2"
                    :class="{ disabled: item.isDeleted }"
                  >
                    {{ item.email }}
                  </p>
                </template>
                <template v-slot:item.status="{ item }">
                  <div class="d-flex align-center">
                    <v-badge
                      dot
                      inline
                      :color="getBadgeColor(item)"
                      :class="{ disabled: item.isDeleted }"
                    />
                    <span
                      class="ms-2"
                      :class="{
                        'red--text': item.isDeleted,
                        disabled: item.isDeleted,
                      }"
                      >{{ item.statusText }}</span
                    >
                  </div>
                </template>
                <template v-slot:item.tags="{ item }">
                  <div class="d-flex align-center flex-wrap mb-1">
                    <v-chip
                      outlined
                      small
                      class="me-2 mt-1"
                      v-for="tag in item.tags"
                      :key="tag.id"
                      ><span class="blue-grey--text text--darken-3 caption">{{
                        tag.name
                      }}</span></v-chip
                    >
                  </div>
                </template>
                <template v-slot:item.adminRole="{ item }">
                  <div v-if="item.isDeleted">
                    <v-chip
                      v-if="item.adminRole.id !== ADMIN_ROLES.NONE"
                      small
                      outlined
                      class="my-2 me-2 disabled"
                      data-testid="organizationUsers-chip-adminRoleDeleted"
                    >
                      <span>{{ item.adminRole.text }}</span>
                    </v-chip>
                  </div>

                  <div
                    v-else
                    data-testid="organizationUsers-div-adminRole"
                  >
                    <v-btn
                      v-if="item.adminRole.id === ADMIN_ROLES.NONE"
                      color="blue"
                      data-testid="organizationUsers-button-assignRole"
                      rounded
                      small
                      text
                      @click="openRoleAssignmentModal(item)"
                    >
                      <v-icon left size="16" class="my-0 py-0"
                        >mdi-plus-circle-outline
                      </v-icon>
                      {{ $t('common.select') }}
                    </v-btn>
                    <v-chip
                      v-else
                      data-testid="organizationUsers-chip-adminRole"
                      outlined
                      small
                      @click="openRoleAssignmentModal(item)"
                    >
                      <span>{{ item.adminRole.text }}</span>
                    </v-chip>
                  </div>
                </template>
                <template v-slot:item.permissions="{ item }">
                  <div v-if="item.isDeleted">
                    <div class="d-flex align-center flex-wrap mb-1">
                      <v-chip
                        small
                        outlined
                        class="mt-1 me-2 disabled"
                        v-for="permission in item.permissions"
                        v-show="permission.value"
                        :key="permission.id"
                      >
                        <span>{{ permission.text }}</span>
                      </v-chip>
                    </div>
                  </div>
                  <v-edit-dialog v-else @close="setPermission(item)">
                    <v-btn
                      small
                      text
                      rounded
                      color="blue"
                      class="mt-1"
                      v-show="!hasPermissions(item.permissions)"
                    >
                      <v-icon left size="16" class="my-0 py-0"
                        >mdi-plus-circle-outline
                      </v-icon>
                      {{ $t('common.select') }}
                    </v-btn>
                    <div class="d-flex align-center flex-wrap mb-1">
                      <v-chip
                        small
                        outlined
                        class="mt-1 me-2"
                        v-for="permission in item.permissions"
                        v-show="permission.value"
                        :key="permission.id"
                        :class="{ disabled: item.isDeleted }"
                      >
                        <span>{{ permission.text }}</span>
                        <v-icon
                          class="ms-1"
                          size="small"
                          color="blue-grey"
                          @click.stop="
                            ;((permission.value = false),
                              removePermission(item, permission))
                          "
                          >mdi-close-circle-outline</v-icon
                        >
                      </v-chip>
                    </div>

                    <template v-slot:input>
                      <div
                        v-for="permission in item.permissions"
                        class="menu-item ps-1 pe-3 py-2 mt-1"
                        :key="permission.id"
                      >
                        <v-checkbox
                          hide-details
                          class="ma-0 pa-0"
                          v-model="permission.value"
                          :ripple="false"
                        >
                          <template v-slot:label>
                            <span
                              class="mx-0 px-0 font-size-13 font-weight-500"
                            >
                              {{ permission.text }}
                            </span>
                          </template>
                        </v-checkbox>
                      </div>
                    </template>
                  </v-edit-dialog>
                </template>
                <template v-slot:item.actions="{ item }">
                  <div class="d-flex">
                    <v-icon
                      v-for="(action, index) in item.actions"
                      :disabled="
                        action.name === 'debug' ? false : item.isDeleted
                      "
                      :class="{
                        'me-2': index !== item.actions.length - 1,
                        disabled:
                          action.name === 'debug' ? false : item.isDeleted,
                      }"
                      :key="action.type"
                      @click="handleClick(action.name, item)"
                      >{{ action.type }}</v-icon
                    >
                  </div>
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <ResetPasswordDialog
      v-if="modals.resetPassword"
      :show="modals.resetPassword"
      :email="selectedAccount.email"
      :isLoading="isReseting"
      @cancel="hideModal('resetPassword')"
      @reset="resetPassword"
    />

    <AddUserModalBulk
      size="lg"
      canAssignRole
      :rolesEnabled="rolesEnabled"
      :show="modals.userModalShow"
      :remainingUsersToAdd="remainingUsersToAdd"
      :organizationId="organizationId"
      @closeModal="hideModal('userModalShow')"
      @onUsersAdded="onUsersAdded"
    />

    <EditAccountDialog
      v-if="!isAccountEmpty"
      :show="modals.editAccount"
      :account="selectedAccount"
      :organizationId="organizationId"
      @update="onEditAccountDialogSubmit"
      @cancel="onCancelAttributesModal"
    />

    <DebugModal
      :show="modals.debugModalShow"
      :item="selectedAccount"
      @close="hideModal('debugModalShow')"
    />

    <DeleteConfirmationModal
      :show="modals.deleteAccount"
      :isLoading="isDeleting"
      :title="$t('common.users.remove_this_account')"
      :text="$t('common.users.delete_account_text')"
      :confirmText="$t('common.button.deactivate')"
      @cancel="hideModal('deleteAccount')"
      @delete="deleteAccount"
    />

    <RoleAssignmentModal
      :show="modals.roleAssignment"
      :user="selectedAccount"
      :sites="organizationStructure?.sites ?? []"
      :siteGroups="organizationStructure?.siteGroups ?? []"
      @cancel="hideModal('roleAssignment')"
      @error="handleRoleAssignmentError"
      @saved="saveRoleAssignment"
    />
  </ViewContainer>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import _ from 'lodash'
import { utils } from 'frontend-js-shared'

import mixin from '@/mixins/mixin.vue'
import adminRoleMixin from '@/mixins/adminRoleMixin'
import api from '@/api'

import EditAccountDialog from '@/components/People/EditAccountDialog.vue'
import AddUserModalBulk from '@/components/People/AddPeopleModal/AddUserModalBulk.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import DebugModal from '@/components/Admin/Organizations/DebugModal.vue'
import ResetPasswordDialog from '@/components/People/ResetPasswordDialog.vue'
import PeopleFilters from '@/components/People/PeopleFilters.vue'
import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'
import ChipAvatar from '@/components/ChipAvatar.vue'
import CreateButton from '@/components/UI/CreateButton.vue'
import SearchInput from '@/components/Core/SearchInput.vue'
import EmptyState from '@/components/People/EmptyState.vue'
import ColumnsSelector from '@/components/ColumnsSelector.vue'
import Button from '@/components/UI/Button'
import UserLicensesCard from './UserLicensesCard.vue'
import OrganizationMenu from './OrganizationMenu.vue'
import RoleAssignmentModal from './RoleAssignmentModal.vue'

const SORT_BY_FIELD = 'updatedTimestamp'
const DEFAULT_SIZE = 20

export default {
  name: 'OrganizationsUsers',

  mixins: [mixin, adminRoleMixin],

  components: {
    AddUserModalBulk,
    Button,
    ChipAvatar,
    ColumnsSelector,
    CreateButton,
    DebugModal,
    DeleteConfirmationModal,
    EditAccountDialog,
    EmptyState,
    OrganizationMenu,
    PeopleFilters,
    ResetPasswordDialog,
    RoleAssignmentModal,
    SearchInput,
    UserLicensesCard,
    ViewContainer,
  },

  data() {
    return {
      isReseting: false,
      breadcrumbs: [],
      headers: [
        {
          text: this.$t('common.name'),
          align: 'start',
          sortable: true,
          value: 'displayname',
          show: true,
        },
        {
          text: this.$t('common.email_one'),
          sortable: false,
          value: 'email',
          show: true,
        },
        {
          text: this.$t('common.status'),
          sortable: true,
          value: 'status',
          show: true,
        },
        {
          text: this.$t('common.admin_role_other'),
          sortable: false,
          value: 'adminRole',
          show: true,
        },
        {
          text: this.$t('organization_users.permission_other'),
          sortable: false,
          value: 'permissions',
          show: true,
        },
        {
          text: this.$t('common.tag_other'),
          sortable: false,
          value: 'tags',
          show: false,
          width: 250,
        },
        {
          text: this.$t('common.action_other'),
          sortable: false,
          value: 'actions',
          show: true,
        },
      ],
      users: [],
      currentAccountsCreated: 0,
      isLoading: true,
      organizationStructure: null,
      modals: {
        userModalShow: false,
        editAccount: false,
        debugModalShow: false,
        deleteAccount: false,
        resetPassword: false,
        roleAssignment: false,
      },
      selectedAccount: {},
      selectedFilters: {
        countryCodes: [],
        sites: [],
        status: '',
        permission: '',
        role: '',
        siteGroups: [],
        tags: [],
        tagGroups: [],
      },
      searchTerms: '',
      totalRecordsInData: 0,
      organizationId: this.getURLParam('organizationId'),
      organization: {},
      footerOptions: {
        itemsPerPageOptions: [20, 50, 100],
      },
      isDeleting: false,
      account: {},
      showFilters: false,
      rolesEnabled: [
        'isOrganizationAdmin',
        'isSiteAdmin',
        'isCoach',
        'canDoFinancialValidation',
        'isRoutineManager',
      ],
    }
  },

  async created() {
    this.organization = await this.getOrganization()
    this.organizationStructure = await this.getOrganizationStructure()
    this.breadcrumbs = [
      {
        href: '/admin/organizations',
        name: this.$t('common.organization_other'),
      },
      {
        href: `/organization-settings/${this.organizationId}`,
        name: this.organization.name,
      },
      { name: this.$t('people.people') },
    ]
  },

  computed: {
    ...mapGetters(['accountId']),

    activeFiltersCount() {
      return Object.keys(this.selectedFilters)?.filter(
        filter => !_.isEmpty(this.selectedFilters?.[filter])
      )?.length
    },

    hasFiltersSelected() {
      return Object.keys(this.selectedFilters).some(
        key => !_.isEmpty(this.selectedFilters[key])
      )
    },

    organizationAuthIsLocal() {
      return this.organization?.authenticationType?.includes('local')
    },

    columns() {
      return this.headers.filter(h => h.show)
    },

    canAddUsers() {
      return this.remainingUsersToAdd > 0
    },

    organizationPlan() {
      return this.organization?.plan
    },

    remainingUsersToAdd() {
      const { licenses } = this.organization
      return licenses - this.currentAccountsCreated
    },

    isAccountEmpty() {
      return _.isEmpty(this.selectedAccount)
    },

    options: {
      get() {
        return {
          itemsLength: this.totalRecordsInData,
          itemsPerPage: DEFAULT_SIZE,
          multiSort: false,
          mustSort: true,
          page: 1,
        }
      },
      set(event) {
        this.getUsers({
          page: event.page,
          itemsPerPage: event.itemsPerPage,
          sortDesc: event.sortDesc[0],
          sortBy: event.sortBy[0],
        })
      },
    },
  },

  methods: {
    ...mapActions({
      getAccounts: 'getAccounts',
      createOrganizationAccount: 'createOrganizationAccount',
      fetchAccount: 'fetchAccount',
      updateAccount: 'updateAccount',
      fetchOrganization: 'getOrganization',
    }),

    showModal(modal) {
      this.modals[modal] = true
    },

    hideModal(modal) {
      this.modals[modal] = false
    },

    clearAllFilters() {
      Object.keys(this.selectedFilters)?.map(
        key => (this.selectedFilters[key] = [])
      )
      this.handleFiltersChange(this.selectedFilters)
    },

    async getOrganizationStructure() {
      const response = await api.organization.getOrganizationStructure(
        this.organizationId
      )
      return response.data
    },

    handleSearch(terms) {
      this.searchTerms = terms
      this.getUsers(this.options)
    },

    onToggleColumn({ index, value }) {
      this.headers[index].show = value
    },

    getBadgeColor(item) {
      if (item.isDeleted) return 'red'
      switch (item.status) {
        case 'joined':
          return 'green lighten-1'
        case 'pending':
          return 'yellow lighten-1'
        case 'created':
          return 'blue-grey lighten-1'
        default:
          return ''
      }
    },

    async getOrganization() {
      return await this.fetchOrganization({
        organizationId: this.organizationId,
      })
    },

    async onFiltersUpdate(payload) {
      this.selectedFilters = payload
      await this.getUsers(this.options)
    },

    hasPermissions(permissions) {
      const perm = permissions.filter(p => p.value === true)
      return Boolean(perm.length)
    },

    handleClick(type, user) {
      this.selectedAccount = user
      switch (type) {
        case 'edit':
          this.modals.editAccount = true
          break
        case 'debug':
          this.modals.debugModalShow = true
          break
        case 'reset':
          this.modals.resetPassword = true
          break
        case 'delete':
          this.modals.deleteAccount = true
          break
      }
    },

    async removePermission(user, permissionToRemove) {
      await this.updateAccount({
        account: user,
        data: { [permissionToRemove.id]: false },
      })
    },

    buildUsers(users) {
      return users.map(user => {
        const permissions = [
          {
            value: user.isCoach,
            text: this.$t('common.roles.coach_one'),
            id: 'isCoach',
            color: 'blue lighten-5',
          },
          {
            value: user.canDoFinancialValidation,
            text: this.$t('common.roles.impact_validator'),
            id: 'canDoFinancialValidation',
            color: 'light-green lighten-5',
          },
          {
            value: user.isRoutineManager,
            text: this.$t('common.roles.routine_manager'),
            id: 'isRoutineManager',
            color: 'purple lighten-5',
          },
        ]

        return {
          ...user,
          name: this.getFullName(user),
          statusText: this.$t(`people.status.${user.status}`),
          adminRole: this.getAdminRole(user),
          actions: _.compact([
            { type: 'mdi-pencil-outline', name: 'edit' },
            { type: 'mdi-magnify-plus-outline', name: 'debug' },
            this.organizationAuthIsLocal && { type: 'restore', name: 'reset' },
            { type: 'mdi-delete-outline', name: 'delete' },
          ]),
          permissions,
        }
      })
    },

    async getUsers(options) {
      this.isLoading = true
      const size = options.itemsPerPage || DEFAULT_SIZE
      const sort = { by: '', order: '' }
      if (options?.sortBy?.length) {
        sort.by = options.sortBy
        sort.order = options.sortDesc ? 'desc' : 'asc'
      } else {
        sort.by = SORT_BY_FIELD
        sort.order = 'desc'
      }

      try {
        const response = await api.account.getAccounts(this.organizationId, {
          limit: options.itemsPerPage || DEFAULT_SIZE,
          from: options.page * size - size,
          filters: this.selectedFilters,
          sort,
          terms: this.searchTerms,
          relations: ['tags'],
        })
        this.users = this.buildUsers(response?.data?.results ?? [])

        this.totalRecordsInData = response?.data?.totalRecordsInData ?? 0
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    async getTotalAccountsInOrganization() {
      const options = {
        organizationId: this.organizationId,
        sort: {
          by: SORT_BY_FIELD,
          order: 'desc',
        },
        filters: {
          isDeleted: false,
        },
      }

      const results = await this.getAccounts(options)
      this.currentAccountsCreated = results.totalRecordsInData
    },

    openRoleAssignmentModal(user) {
      this.selectedAccount = user
      this.showModal('roleAssignment')
    },

    updateUserInList(user, data) {
      const userId = utils.getEntityId(user)
      const index = this.users.findIndex(
        currentUser => utils.getEntityId(currentUser) === userId
      )
      if (index === -1) return
      this.users.splice(index, 1, {
        ...user,
        ...data,
        adminRole: this.getAdminRole(data),
      })
    },

    saveRoleAssignment({ user, data }) {
      this.updateUserInList(user, data)
      this.hideModal('roleAssignment')
      this.showSuccess(this.$t('people.admin_role_updated_successfully'))
    },

    handleRoleAssignmentError() {
      this.showError(this.$t('people.admin_role_update_failed'))
    },

    async setPermission(user) {
      const data = {}
      user.permissions.forEach(permission => {
        data[permission.id] = permission.value
      })
      await this.updateAccount({
        account: user,
        data,
      })
    },

    async resetPassword(password) {
      try {
        this.isReseting = true
        await api.account.resetPassword({
          organizationId: this.organization._id,
          userId: this.selectedAccount._id,
          password,
        })
        this.showSuccess(this.$t('people.password_successfully_reset'))
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isReseting = false
        this.modals.resetPassword = false
      }
    },

    async deleteAccount() {
      try {
        this.isDeleting = true
        await this.updateAccount({
          account: this.selectedAccount,
          data: {
            isDeleted: true,
          },
        })
        this.showSuccess(this.$t('people.account_successfully_deleted'))
        await this.getUsers(this.options)
        this.modals.deleteAccount = false
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isDeleting = false
      }
    },

    openEditAccountDialog(val) {
      this.selectedAccount = val
      this.modals.editAccount = true
    },

    async onEditAccountDialogSubmit(account) {
      try {
        const accountToUpdate = { ...account, ...account.location }
        delete accountToUpdate.countryCode

        await api.account.update(
          account?.organization?._id ?? account?.organization,
          account?._id,
          accountToUpdate
        )
        if (account._id === this.accountId) await this.getMe()

        this.modals.editAccount = false
        this.selectedAccount = {}
      } catch (err) {
        this.showError(err.response.data.message)
      } finally {
        await this.getUsers(this.options)
      }
    },

    onCancelAttributesModal() {
      this.modals.editAccount = false
      this.selectedAccount = {}
    },

    async onUsersAdded() {
      this.isLoading = true
      await this.getUsers(this.options)
      await this.getTotalAccountsInOrganization()
    },

    closeAddUsersModal() {
      this.modals.userModalShow = false
    },

    setInitialColumns(selectedColumns) {
      this.headers = this.getInitialSelectedTableColumns(
        this.headers,
        selectedColumns
      )
    },
  },
}
</script>
<style lang="scss" scoped>
.disabled {
  opacity: 0.3;
}
.menu-item {
  border-radius: 4px;
  &:first-child {
    margin-top: 0 !important;
  }
  &:hover {
    background-color: $color-blue-grey-lighten3;
  }
}
</style>
<style lang="scss">
.v-small-dialog__content {
  padding: 4px;
}
</style>
